// src/components/shell/popovers/SettingsPopover.tsx
// Gear trigger + popup with a small category list (Voice / Appearance / Leads).
// Selecting a category swaps the panel body for the matching settings section.
// Section bodies are reused as-is from the legacy SettingsDrawer, so behavior
// stays identical to the gear-icon path during the transition.
import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { useAppTheme } from "@/contexts/ThemeContext";
import { SettingsDrawerAppearanceSection } from "@/components/common/SettingsDrawerAppearanceSection";
import { SettingsDrawerLeadsSection } from "@/components/common/SettingsDrawerLeadsSection";
import { SettingsDrawerVoiceSection } from "@/components/common/SettingsDrawerVoiceSection";
import { SettingsDrawerActivityDockSection } from "@/components/common/SettingsDrawerActivityDockSection";
import { SettingsDrawerCapabilitiesSection } from "@/components/common/SettingsDrawerCapabilitiesSection";
import { useActivityDockAssistLevel } from "@/components/activity/useActivityDockAssistLevel";
import { useFireflyVisualIntensity } from "@/components/activity/useFireflyVisualIntensity";
import { usePrefersReducedMotion } from "@/components/activity/firefly/usePrefersReducedMotion";
import { useUserCapabilitiesEditor } from "@/hooks/useUserCapabilitiesEditor";
import { CAPABILITY_DESCRIPTIONS } from "@/lib/capabilities/capabilities";
import { SidebarRailButton } from "@/components/shell/Sidebar/SidebarRailButton";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import {
  getSpeakMode,
  getVoiceEnabled,
  getVoiceProvider,
  setSpeakMode as persistSpeakMode,
  setVoiceEnabled as persistVoiceEnabled,
  setVoiceProvider as persistVoiceProvider,
  subscribeVoiceSettings,
} from "@/lib/speech/voiceSettings";
import { railPopoverPanelStyle, useRailPopover } from "./useRailPopover";

export type SettingsPopoverLeadsControls = {
  emptyLeadAutoDeleteEnabled: boolean;
  setEmptyLeadAutoDeleteEnabled: (v: boolean) => void;
  syncedCount: number;
  viableCount: number;
  isSyncing: boolean;
  onSyncAllLeads: () => Promise<void>;
};

export type SettingsPopoverProps = {
  expanded: boolean;
  onAfterAction?: () => void;
  /** When provided, the Leads category appears in the category list. */
  leadsControls?: SettingsPopoverLeadsControls | null;
};

type CategoryId =
  | "list"
  | "voice"
  | "appearance"
  | "leads"
  | "activityDock"
  | "responsibilities";

export function SettingsPopover({ expanded, onAfterAction, leadsControls = null }: SettingsPopoverProps) {
  const { open, toggle, setOpen, triggerRef, panelRef, position } = useRailPopover();
  const { theme, setTheme } = useAppTheme();
  const [voiceEnabled, setVoiceEnabledState] = useState(getVoiceEnabled);
  const [voiceProvider, setVoiceProviderState] = useState(getVoiceProvider);
  const [speakMode, setSpeakModeState] = useState(getSpeakMode);
  const [view, setView] = useState<CategoryId>("list");
  const { level: assistLevel, setLevel: setAssistLevel } =
    useActivityDockAssistLevel();
  const { intensity: visualIntensity, setIntensity: setVisualIntensity } =
    useFireflyVisualIntensity();
  const prefersReducedMotion = usePrefersReducedMotion();
  const {
    overrides: capabilityOverrides,
    isEffective: isCapabilityEffective,
    setOverride: setCapabilityOverride,
  } = useUserCapabilitiesEditor();

  useEffect(() => {
    return subscribeVoiceSettings((snap) => {
      setVoiceEnabledState(snap.enabled);
      setVoiceProviderState(snap.provider);
      setSpeakModeState(snap.speakMode);
    });
  }, []);

  const handleTriggerClick = () => {
    // Reset to the category list whenever the popover is (re-)opened from the trigger.
    if (!open) setView("list");
    toggle();
  };

  return (
    <>
      <SidebarRailButton
        ref={triggerRef}
        expanded={expanded}
        glyph="⚙"
        label="Settings"
        active={open}
        onClick={handleTriggerClick}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Settings"
        data-onboarding-anchor="settings"
        {...{ [FIREFLY_TARGET_ATTR]: "sidebar-settings" }}
      />
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Settings"
              className="rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-zinc-950 dark:text-gray-100"
              style={railPopoverPanelStyle(position)}
            >
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                {view !== "list" ? (
                  <button
                    type="button"
                    onClick={() => setView("list")}
                    className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    aria-label="Back to settings list"
                  >
                    ←
                  </button>
                ) : null}
                <h2 className="text-sm font-semibold">
                  {view === "list"
                    ? "Settings"
                    : view === "voice"
                      ? "Voice"
                      : view === "appearance"
                        ? "Appearance"
                        : view === "activityDock"
                          ? "Firefly"
                          : view === "responsibilities"
                            ? "Responsibilities"
                            : "Leads"}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onAfterAction?.();
                  }}
                  aria-label="Close settings"
                  className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-base leading-none"
                >
                  ✕
                </button>
              </div>

              <div className="px-4 py-3">
                {view === "list" ? (
                  <ul className="space-y-1">
                    <CategoryItem label="Voice" hint="Spoken feedback" onClick={() => setView("voice")} />
                    <CategoryItem label="Appearance" hint="Theme" onClick={() => setView("appearance")} />
                    <CategoryItem
                      label="Firefly"
                      hint="Assist level + motion intensity"
                      onClick={() => setView("activityDock")}
                    />
                    <CategoryItem
                      label="Responsibilities"
                      hint="Tasks you do for your org"
                      onClick={() => setView("responsibilities")}
                    />
                    {leadsControls ? (
                      <CategoryItem
                        label="Leads"
                        hint="Auto-delete + Supabase sync"
                        onClick={() => setView("leads")}
                      />
                    ) : null}
                  </ul>
                ) : null}

                {view === "voice" ? (
                  <SettingsDrawerVoiceSection
                    voiceEnabled={voiceEnabled}
                    setVoiceEnabled={(v) => {
                      persistVoiceEnabled(v);
                      setVoiceEnabledState(v);
                    }}
                    voiceProvider={voiceProvider}
                    setVoiceProvider={(p) => {
                      persistVoiceProvider(p);
                      setVoiceProviderState(p);
                    }}
                    speakMode={speakMode}
                    setSpeakMode={(m) => {
                      persistSpeakMode(m);
                      setSpeakModeState(m);
                    }}
                  />
                ) : null}

                {view === "appearance" ? (
                  <SettingsDrawerAppearanceSection theme={theme} setTheme={setTheme} />
                ) : null}

                {view === "activityDock" ? (
                  <SettingsDrawerActivityDockSection
                    level={assistLevel}
                    setLevel={setAssistLevel}
                    visualIntensity={visualIntensity}
                    setVisualIntensity={setVisualIntensity}
                    visualIntensityCapped={
                      prefersReducedMotion && visualIntensity !== "calm"
                    }
                  />
                ) : null}

                {view === "responsibilities" ? (
                  <SettingsDrawerCapabilitiesSection
                    capabilities={CAPABILITY_DESCRIPTIONS}
                    overrides={capabilityOverrides}
                    isEffective={isCapabilityEffective}
                    setOverride={setCapabilityOverride}
                  />
                ) : null}

                {view === "leads" && leadsControls ? (
                  <SettingsDrawerLeadsSection
                    emptyLeadAutoDeleteEnabled={leadsControls.emptyLeadAutoDeleteEnabled}
                    setEmptyLeadAutoDeleteEnabled={leadsControls.setEmptyLeadAutoDeleteEnabled}
                    syncedCount={leadsControls.syncedCount}
                    viableCount={leadsControls.viableCount}
                    isSyncing={leadsControls.isSyncing}
                    onSyncAllLeads={leadsControls.onSyncAllLeads}
                  />
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function CategoryItem({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        <span className="font-medium">{label}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{hint}</span>
      </button>
    </li>
  );
}
